import { ApolloQueryResult } from '@apollo/client';
import {
  Text,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalHeader,
  useDisclosure,
  TextInput,
  ModalFooter,
  Tag,
  Flex,
} from '@televet/kibble-ui';
import React from 'react';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import {
  useSearchAvailableBandwidthNumbersQuery,
  useOrderBandwidthPhoneNumberMutation,
  GetClinicPhoneNumbersQuery,
} from 'shared/types/graphql';
import { formatPhoneNumber } from 'shared/utils';

const AddBandwidthNumber = ({
  refetchPhoneNumbers,
  totalNumbers,
  onAddNumberError,
  isClinicDataComplete,
}: {
  refetchPhoneNumbers: () => Promise<ApolloQueryResult<GetClinicPhoneNumbersQuery>>;
  totalNumbers: number;
  onAddNumberError: () => void;
  isClinicDataComplete: boolean;
}): JSX.Element => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedNumber, setSelectedNumber] = React.useState<string>('');
  const textInputRef = React.useRef<HTMLInputElement>(null);

  const {
    data: availableBandwidthNumbers,
    loading: loadingAvailableBandwidthNumbers,
    refetch,
    error,
  } = useSearchAvailableBandwidthNumbersQuery({
    variables: { data: { code: '91902', quantity: '12' } },
    fetchPolicy: GraphQLFetchPolicies.NoCache,
  });

  const [orderBandwidthPhoneNumber] = useOrderBandwidthPhoneNumberMutation();

  const handleSearch = (): void => {
    refetch({
      data: {
        code: textInputRef.current?.value || '91902',
        quantity: '12',
      },
    });
  };

  const handleOrderNumber = async (): Promise<void> => {
    await orderBandwidthPhoneNumber({
      variables: {
        data: {
          phoneNumber: selectedNumber,
        },
      },
    });
    await refetchPhoneNumbers();
    setSelectedNumber('');
    onClose();
  };

  const handleOpenModal = (): void => {
    if (totalNumbers >= 2 || !isClinicDataComplete) {
      onAddNumberError();
      return;
    }
    onOpen();
  };

  return (
    <div className="AddBandwidthNumber">
      <Modal
        isOpen={isOpen}
        onClose={(): void => {
          setSelectedNumber('');
          if (textInputRef.current) {
            textInputRef.current.value = '';
          }
          onClose();
        }}
        isCentered={true}
        size={!selectedNumber ? 'xl' : 'md'}
      >
        <ModalHeader>{!selectedNumber ? 'Search Available Numbers' : 'Purchase Number'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={5}>
          {!selectedNumber ? (
            <>
              <TextInput ref={textInputRef} placeholder="Zip Code or area code" />
              <Flex justifyContent={'space-evenly'} flexWrap={'wrap'} padding="0 12px">
                {!error ? (
                  loadingAvailableBandwidthNumbers ? (
                    <Flex margin="48px">
                      <LoadingSpinner />
                    </Flex>
                  ) : (
                    <>
                      {availableBandwidthNumbers?.searchPhoneNumber?.phoneNumbers?.map((number) => {
                        return (
                          number && (
                            <Tag
                              key={number}
                              label={formatPhoneNumber(number)}
                              marginTop={'12px'}
                              cursor={'pointer'}
                              onClick={(): void => setSelectedNumber(number)}
                            />
                          )
                        );
                      })}
                    </>
                  )
                ) : (
                  <Text variant="danger">Error: {error?.message}</Text>
                )}
              </Flex>
            </>
          ) : (
            <Flex justifyContent={'center'}>
              <Text size="2xl" fontWeight="bold">
                {formatPhoneNumber(selectedNumber)}
              </Text>
            </Flex>
          )}
          <ModalFooter marginBottom={'8px'}>
            {!selectedNumber ? (
              <Button onClick={handleSearch}>Search Numbers</Button>
            ) : (
              <Flex justifyContent={'space-between'} flex={'1'}>
                <Button variant="secondary" onClick={(): void => setSelectedNumber('')}>
                  Cancel
                </Button>
                <Button onClick={handleOrderNumber}>Purchase Number</Button>
              </Flex>
            )}
          </ModalFooter>
        </ModalBody>
      </Modal>
      <Button size="sm" variant={'secondary'} iconName={'plus'} onClick={handleOpenModal}>
        Add New Bandwidth Number
      </Button>
    </div>
  );
};

export default AddBandwidthNumber;
